import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { login, logout } from '../services/auth';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authTokens, setAuthTokens] = useState(() => {
    const tokens = localStorage.getItem('authTokens');
    return tokens ? JSON.parse(tokens) : null;
  });
  const [user, setUser] = useState(null);
  
  // Efeito para carregar o usuário quando o token muda ou na inicialização
  useEffect(() => {
    if (authTokens) {
      fetchUserProfile(authTokens.access);
    }
  }, [authTokens]);

  // Função para obter o perfil do usuário
  const fetchUserProfile = async (accessToken) => {
    try {
      const profileResponse = await axios.get("/api/profile/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      // Decodificar o token para obter as informações
      const decoded = jwtDecode(accessToken);
      let groups = [];
      if (decoded.groups) {
        // Se o payload já tiver um array de grupos
        groups = decoded.groups;
      } else if (decoded.group) {
        // Se for uma string, dividir por vírgula
        groups = decoded.group.split(',');
      }
      setUser({ ...profileResponse.data, groups });
    } catch (error) {
      console.error("Erro ao buscar perfil do usuário:", error);
      
      // Se o erro for 401 (não autorizado), pode ser que o token expirou
      if (error.response && error.response.status === 401) {
        // Tentar refresh do token ou fazer logout
        logoutUser();
      }
    }
  };

  // Função de login
  const loginUser = async (username, password) => {
    try {
      const response = await login({ username, password });
      
      // Salvar tokens e atualizar estado
      const tokens = response.data;
      localStorage.setItem('authTokens', JSON.stringify(tokens));
      setAuthTokens(tokens);
      
      // O fetchUserProfile será chamado automaticamente pelo useEffect
      return true;
    } catch (error) {
      console.error("Erro no login:", error);
      return false;
    }
  };

  // Função de logout
  const logoutUser = async () => {
    try {
      if (authTokens?.refresh) {
        await logout(authTokens.refresh);
      }
      localStorage.removeItem('authTokens');
      setAuthTokens(null);
      setUser(null);
    } catch (error) {
      console.error("Erro no logout:", error);
      // Limpar os estados mesmo se houver erro
      localStorage.removeItem('authTokens');
      setAuthTokens(null);
      setUser(null);
    }
  };

  // Função para renovar o token de acesso usando o refresh token
  const refreshAccessToken = async (refreshToken) => {
    try {
      const response = await axios.post("/api/token/refresh/", { refresh: refreshToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Verificar se o token está expirado
  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    } catch (error) {
      return true;
    }
  };

  // Adicionar interceptor para verificar token antes de cada requisição
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        if (authTokens) {
          // Verificar se o token não expirou
          if (!isTokenExpired(authTokens.access)) {
            config.headers.Authorization = `Bearer ${authTokens.access}`;
          } else if (authTokens.refresh) {
            try {
              const response = await refreshAccessToken(authTokens.refresh);
              const tokens = response.data;
              localStorage.setItem('authTokens', JSON.stringify(tokens));
              setAuthTokens(tokens);
              config.headers.Authorization = `Bearer ${tokens.access}`;
            } catch (error) {
              console.error("Erro ao renovar token", error);
              logoutUser();
            }
          } else {
            logoutUser();
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      // Limpar o interceptor quando o componente for desmontado
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, [authTokens]);

  const contextData = {
    user,
    loginUser,
    logoutUser,
    authTokens,
  };

  return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
};